import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CurrencyDollarIcon,
  UsersIcon,
  ClockIcon,
  StarIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  EyeIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  BoltIcon,
  ScaleIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
  RadialBarChart,
  RadialBar,
  ReferenceLine
} from 'recharts';

interface FilterProps {
  dateRange: { start: string; end: string; preset: string };
  departments: string[];
  metrics: string[];
  detailLevel: string;
  displayType: string;
  currency: string;
}

interface KPIsAnalysisProps {
  filters: FilterProps;
}

const KPIsAnalysis: React.FC<KPIsAnalysisProps> = ({ filters }) => {
  const { t, direction } = useLanguage();
  const [selectedCategory, setSelectedCategory] = useState('financial');
  const [viewType, setViewType] = useState('current');

  // Financial KPIs
  const financialKPIs = [
    {
      name: 'إجمالي الإيرادات',
      value: 2847500,
      target: 2800000,
      previousValue: 2650000,
      unit: 'ر.س',
      format: 'currency',
      trend: 'up',
      change: 7.5,
      status: 'excellent',
      icon: CurrencyDollarIcon,
      color: 'from-green-500 to-green-600',
      description: 'إجمالي الإيرادات المحققة'
    },
    {
      name: 'صافي الربح',
      value: 854250,
      target: 840000,
      previousValue: 795000,
      unit: 'ر.س',
      format: 'currency',
      trend: 'up',
      change: 7.5,
      status: 'excellent',
      icon: ArrowTrendingUpIcon,
      color: 'from-blue-500 to-blue-600',
      description: 'صافي الربح بعد خصم جميع المصاريف'
    },
    {
      name: 'هامش الربح الإجمالي',
      value: 32.5,
      target: 30.0,
      previousValue: 30.8,
      unit: '%',
      format: 'percentage',
      trend: 'up',
      change: 5.5,
      status: 'excellent',
      icon: ChartBarIcon,
      color: 'from-purple-500 to-purple-600',
      description: 'نسبة الربح الإجمالي إلى الإيرادات'
    },
    {
      name: 'العائد على الاستثمار',
      value: 18.7,
      target: 15.0,
      previousValue: 16.2,
      unit: '%',
      format: 'percentage',
      trend: 'up',
      change: 15.4,
      status: 'excellent',
      icon: ScaleIcon,
      color: 'from-orange-500 to-orange-600',
      description: 'العائد على رؤوس الأموال المستثمرة'
    }
  ];

  // Operational KPIs
  const operationalKPIs = [
    {
      name: 'رضا العملاء',
      value: 4.2,
      target: 4.0,
      previousValue: 3.9,
      unit: '/5',
      format: 'rating',
      trend: 'up',
      change: 7.7,
      status: 'excellent',
      icon: StarIcon,
      color: 'from-yellow-500 to-yellow-600',
      description: 'متوسط تقييم رضا العملاء'
    },
    {
      name: 'معدل الاحتفاظ بالعملاء',
      value: 89.5,
      target: 85.0,
      previousValue: 87.2,
      unit: '%',
      format: 'percentage',
      trend: 'up',
      change: 2.6,
      status: 'excellent',
      icon: UsersIcon,
      color: 'from-indigo-500 to-indigo-600',
      description: 'نسبة العملاء المحتفظ بهم'
    },
    {
      name: 'متوسط وقت الاستجابة',
      value: 2.3,
      target: 3.0,
      previousValue: 2.8,
      unit: 'ساعة',
      format: 'time',
      trend: 'down',
      change: -17.9,
      status: 'excellent',
      icon: ClockIcon,
      color: 'from-green-500 to-green-600',
      description: 'متوسط وقت الرد على استفسارات العملاء'
    },
    {
      name: 'معدل إنجاز المشاريع',
      value: 92.3,
      target: 90.0,
      previousValue: 88.7,
      unit: '%',
      format: 'percentage',
      trend: 'up',
      change: 4.1,
      status: 'excellent',
      icon: CheckCircleIcon,
      color: 'from-teal-500 to-teal-600',
      description: 'نسبة المشاريع المنجزة في الوقت المحدد'
    }
  ];

  // Sales KPIs
  const salesKPIs = [
    {
      name: 'عدد العملاء الجدد',
      value: 156,
      target: 120,
      previousValue: 134,
      unit: 'عميل',
      format: 'number',
      trend: 'up',
      change: 16.4,
      status: 'excellent',
      icon: UsersIcon,
      color: 'from-blue-500 to-blue-600',
      description: 'عدد العملاء الجدد المكتسبين'
    },
    {
      name: 'متوسط قيمة الطلب',
      value: 18250,
      target: 15000,
      previousValue: 16800,
      unit: 'ر.س',
      format: 'currency',
      trend: 'up',
      change: 8.6,
      status: 'excellent',
      icon: CurrencyDollarIcon,
      color: 'from-green-500 to-green-600',
      description: 'متوسط قيمة الطلب الواحد'
    },
    {
      name: 'معدل التحويل',
      value: 3.8,
      target: 3.0,
      previousValue: 3.2,
      unit: '%',
      format: 'percentage',
      trend: 'up',
      change: 18.8,
      status: 'excellent',
      icon: ArrowTrendingUpIcon,
      color: 'from-purple-500 to-purple-600',
      description: 'نسبة تحويل الزوار إلى عملاء'
    },
    {
      name: 'دورة المبيعات',
      value: 28,
      target: 35,
      previousValue: 32,
      unit: 'يوم',
      format: 'days',
      trend: 'down',
      change: -12.5,
      status: 'excellent',
      icon: ClockIcon,
      color: 'from-orange-500 to-orange-600',
      description: 'متوسط مدة دورة المبيعات'
    }
  ];

  // Marketing KPIs
  const marketingKPIs = [
    {
      name: 'تكلفة اكتساب العميل',
      value: 850,
      target: 1000,
      previousValue: 920,
      unit: 'ر.س',
      format: 'currency',
      trend: 'down',
      change: -7.6,
      status: 'excellent',
      icon: CurrencyDollarIcon,
      color: 'from-green-500 to-green-600',
      description: 'متوسط تكلفة اكتساب العميل الواحد'
    },
    {
      name: 'عائد الاستثمار التسويقي',
      value: 4.2,
      target: 3.5,
      previousValue: 3.8,
      unit: 'x',
      format: 'multiplier',
      trend: 'up',
      change: 10.5,
      status: 'excellent',
      icon: ArrowTrendingUpIcon,
      color: 'from-blue-500 to-blue-600',
      description: 'عائد كل ريال مستثمر في التسويق'
    },
    {
      name: 'معدل النقر',
      value: 2.8,
      target: 2.0,
      previousValue: 2.3,
      unit: '%',
      format: 'percentage',
      trend: 'up',
      change: 21.7,
      status: 'excellent',
      icon: EyeIcon,
      color: 'from-purple-500 to-purple-600',
      description: 'معدل النقر على الإعلانات'
    },
    {
      name: 'معدل التفاعل',
      value: 5.6,
      target: 4.0,
      previousValue: 4.8,
      unit: '%',
      format: 'percentage',
      trend: 'up',
      change: 16.7,
      status: 'excellent',
      icon: BoltIcon,
      color: 'from-yellow-500 to-yellow-600',
      description: 'معدل التفاعل مع المحتوى'
    }
  ];

  // KPI trend data
  const kpiTrendData = [
    { month: 'يناير', revenue: 2100000, profit: 630000, customers: 1350, satisfaction: 3.8 },
    { month: 'فبراير', revenue: 2300000, profit: 690000, customers: 1420, satisfaction: 3.9 },
    { month: 'مارس', revenue: 2500000, profit: 750000, customers: 1480, satisfaction: 4.0 },
    { month: 'أبريل', revenue: 2400000, profit: 720000, customers: 1520, satisfaction: 4.1 },
    { month: 'مايو', revenue: 2600000, profit: 780000, customers: 1580, satisfaction: 4.1 },
    { month: 'يونيو', revenue: 2847500, profit: 854250, customers: 1680, satisfaction: 4.2 }
  ];

  // KPI performance summary
  const performanceSummary = [
    { category: 'المالية', achieved: 95, total: 100, color: '#10B981' },
    { category: 'التشغيلية', achieved: 88, total: 100, color: '#3B82F6' },
    { category: 'المبيعات', achieved: 92, total: 100, color: '#8B5CF6' },
    { category: 'التسويق', achieved: 87, total: 100, color: '#F59E0B' }
  ];

  const getCurrentKPIs = () => {
    switch (selectedCategory) {
      case 'financial':
        return financialKPIs;
      case 'operational':
        return operationalKPIs;
      case 'sales':
        return salesKPIs;
      case 'marketing':
        return marketingKPIs;
      default:
        return financialKPIs;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'excellent':
        return 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-300';
      case 'good':
        return 'text-blue-600 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300';
      case 'warning':
        return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'critical':
        return 'text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-300';
      default:
        return 'text-gray-600 bg-gray-100 dark:bg-gray-900/30 dark:text-gray-300';
    }
  };

  const formatValue = (value: number, format: string, unit: string) => {
    switch (format) {
      case 'currency':
        return `${(value / 1000).toLocaleString()} ألف ${unit}`;
      case 'percentage':
        return `${value.toFixed(1)}${unit}`;
      case 'rating':
        return `${value.toFixed(1)}${unit}`;
      case 'time':
        return `${value.toFixed(1)} ${unit}`;
      case 'days':
        return `${value} ${unit}`;
      case 'multiplier':
        return `${value.toFixed(1)}${unit}`;
      case 'number':
        return `${value} ${unit}`;
      default:
        return `${value} ${unit}`;
    }
  };

  const getTargetAchievement = (value: number, target: number, format: string) => {
    if (format === 'time' || format === 'days' || (format === 'currency' && target > value)) {
      // For metrics where lower is better
      return target > 0 ? ((target - value) / target * 100 + 100) : 100;
    } else {
      // For metrics where higher is better
      return target > 0 ? (value / target * 100) : 100;
    }
  };

  return (
    <div className="space-y-6">
      {/* KPI Category Selector */}
      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { key: 'financial', label: 'المؤشرات المالية', icon: CurrencyDollarIcon },
          { key: 'operational', label: 'المؤشرات التشغيلية', icon: ChartBarIcon },
          { key: 'sales', label: 'مؤشرات المبيعات', icon: ArrowTrendingUpIcon },
          { key: 'marketing', label: 'مؤشرات التسويق', icon: EyeIcon }
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setSelectedCategory(key)}
            className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              selectedCategory === key
                ? 'bg-blue-600 text-white shadow-lg'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <Icon className="w-4 h-4 ml-2 rtl:ml-0 rtl:mr-2" />
            {label}
          </button>
        ))}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {getCurrentKPIs().map((kpi, index) => {
          const Icon = kpi.icon;
          const achievement = getTargetAchievement(kpi.value, kpi.target, kpi.format);
          
          return (
            <motion.div
              key={kpi.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-all duration-300"
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-10 h-10 bg-gradient-to-r ${kpi.color} rounded-lg p-2`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <div className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(kpi.status)}`}>
                  {kpi.status === 'excellent' ? 'ممتاز' : kpi.status === 'good' ? 'جيد' : 'تحذير'}
                </div>
              </div>
              
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                {kpi.name}
              </h3>
              
              <div className="flex items-baseline space-x-2 rtl:space-x-reverse mb-3">
                <span className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatValue(kpi.value, kpi.format, kpi.unit)}
                </span>
                <div className="flex items-center">
                  {kpi.trend === 'up' ? (
                    <ArrowUpIcon className="w-4 h-4 text-green-500" />
                  ) : (
                    <ArrowDownIcon className="w-4 h-4 text-green-500" />
                  )}
                  <span className="text-sm font-medium text-green-600 dark:text-green-400 ml-1">
                    {Math.abs(kpi.change).toFixed(1)}%
                  </span>
                </div>
              </div>
              
              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500 dark:text-gray-400">المستهدف:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {formatValue(kpi.target, kpi.format, kpi.unit)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500 dark:text-gray-400">الإنجاز:</span>
                  <span className="font-medium text-blue-600 dark:text-blue-400">
                    {achievement.toFixed(1)}%
                  </span>
                </div>
              </div>
              
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-3">
                <div 
                  className={`h-2 rounded-full transition-all duration-1000 ${
                    achievement >= 100 ? 'bg-green-500' : 
                    achievement >= 80 ? 'bg-blue-500' : 
                    achievement >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${Math.min(achievement, 100)}%` }}
                ></div>
              </div>
              
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {kpi.description}
              </p>
            </motion.div>
          );
        })}
      </div>

      {/* KPI Performance Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            ملخص أداء المؤشرات
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <RadialBarChart cx="50%" cy="50%" innerRadius="20%" outerRadius="80%" data={performanceSummary}>
              <RadialBar
                label={{ position: 'insideStart', fill: '#fff', fontSize: 12 }}
                background

                dataKey="achieved"
              />
              <Tooltip
                formatter={(value: any, name: any, props: any) => [
                  `${value}%`, 
                  props.payload.category
                ]}
                labelStyle={{ color: '#374151' }}
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}
              />
            </RadialBarChart>
          </ResponsiveContainer>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            اتجاه المؤشرات الرئيسية
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={kpiTrendData}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis 
                dataKey="month" 
                className="text-xs"
                tick={{ fontSize: 12 }}
              />
              <YAxis 
                className="text-xs"
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => `${(value / 1000000).toFixed(1)}م`}
              />
              <Tooltip
                formatter={(value: any, name: any) => [
                  name === 'revenue' ? `${(value / 1000).toLocaleString()} ألف ر.س` :
                  name === 'profit' ? `${(value / 1000).toLocaleString()} ألف ر.س` :
                  name === 'customers' ? `${value.toLocaleString()} عميل` :
                  `${value.toFixed(1)}/5`,
                  name === 'revenue' ? 'الإيرادات' :
                  name === 'profit' ? 'الأرباح' :
                  name === 'customers' ? 'العملاء' : 'الرضا'
                ]}
                labelStyle={{ color: '#374151' }}
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}
              />
              <Line
                type="monotone"
                dataKey="revenue"
                stroke="#10B981"
                strokeWidth={3}
                name="revenue"
                dot={{ fill: '#10B981', strokeWidth: 2, r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="profit"
                stroke="#3B82F6"
                strokeWidth={3}
                name="profit"
                dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* KPI Alerts and Recommendations */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <ExclamationTriangleIcon className="w-5 h-5 ml-2 rtl:ml-0 rtl:mr-2" />
          تنبيهات وتوصيات المؤشرات
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="border border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20 rounded-lg p-4">
            <div className="flex items-start space-x-3 rtl:space-x-reverse">
              <CheckCircleIcon className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                  أداء ممتاز في الإيرادات
                </h4>
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                  تم تجاوز المستهدف بنسبة 1.7% مع نمو مستمر
                </p>
                <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                  مواصلة الاستراتيجية الحالية
                </span>
              </div>
            </div>
          </div>

          <div className="border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20 rounded-lg p-4">
            <div className="flex items-start space-x-3 rtl:space-x-reverse">
              <BoltIcon className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                  تحسن في رضا العملاء
                </h4>
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                  ارتفاع التقييم إلى 4.2/5 مع تحسن مستمر
                </p>
                <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                  الاستثمار في تحسين الخدمة
                </span>
              </div>
            </div>
          </div>

          <div className="border border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20 rounded-lg p-4">
            <div className="flex items-start space-x-3 rtl:space-x-reverse">
              <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                  مراقبة تكلفة الاكتساب
                </h4>
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                  تحسن في التكلفة لكن يحتاج مراقبة مستمرة
                </p>
                <span className="text-xs text-yellow-600 dark:text-yellow-400 font-medium">
                  تحسين كفاءة القنوات التسويقية
                </span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default KPIsAnalysis;
